import { useEffect, useId, useRef } from 'react'
import {
  Overlay,
  ModalContainer,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from './ConfirmModal.styles'
import BaseButton from '../Buttons/BaseButton'
import Portal from '../Basics/Portal'

const ConfirmModal = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  confirmVariant = 'primary',
}) => {
  const titleId = useId()
  const descriptionId = useId()
  const dialogRef = useRef(null)
  const cancelRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return undefined

    const previouslyFocused = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusTimer = window.setTimeout(() => {
      cancelRef.current?.focus()
    }, 0)

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCancel?.()
        return
      }

      if (event.key !== 'Tab' || !dialogRef.current) return

      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus()
      }
    }
  }, [isOpen, onCancel])

  if (!isOpen) return null

  return (
    <Portal>
      <Overlay
        onClick={onCancel}
        role="presentation"
      >
        <ModalContainer
          ref={dialogRef}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          onClick={(event) => event.stopPropagation()}
        >
          <ModalHeader id={titleId}>{title}</ModalHeader>
          <ModalBody id={descriptionId}>{message}</ModalBody>
          <ModalFooter>
            <BaseButton
              ref={cancelRef}
              type="button"
              onClick={onCancel}
              variant="secondary"
            >
              {cancelLabel}
            </BaseButton>
            <BaseButton
              type="button"
              onClick={onConfirm}
              variant={confirmVariant}
            >
              {confirmLabel}
            </BaseButton>
          </ModalFooter>
        </ModalContainer>
      </Overlay>
    </Portal>
  )
}

export default ConfirmModal
